var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef } from 'react';
import useSharedWebSocket from './useSharedWebSocket';
/**
 * Hook for sending messages via WebSocket instead of HTTP GraphQL
 */
export const useWebSocketMessage = () => {
    const { subscribe, unsubscribe, sendMessage: sendWebSocketMessage, isConnected, } = useSharedWebSocket();
    const pendingMessages = useRef(new Map());
    const subscriberId = useRef('websocket-message-sender');
    const sentMessagesRef = useRef(new Set());
    // Subscribe to shared WebSocket
    useEffect(() => {
        const handleMessage = (data) => {
            if (data.type === 'connection_ack') {
                return;
            }
            if (data.type === 'message_sent' && data.id) {
                const pending = pendingMessages.current.get(data.id);
                if (pending) {
                    pending.resolve(data.payload);
                    pendingMessages.current.delete(data.id);
                }
                return;
            }
            if (data.type === 'error' && data.id) {
                const pending = pendingMessages.current.get(data.id);
                if (pending) {
                    const errorMessage = data.payload && data.payload.length > 0 ? data.payload[0].message : 'Unknown error';
                    pending.reject(new Error(errorMessage));
                    pendingMessages.current.delete(data.id);
                }
            }
        };
        const handleConnectionChange = (_connected) => { };
        const currentSubscriberId = subscriberId.current;
        subscribe({
            id: currentSubscriberId,
            onMessage: handleMessage,
            onConnectionChange: handleConnectionChange,
        });
        return () => {
            unsubscribe(currentSubscriberId);
        };
    }, [subscribe, unsubscribe]);
    const sendMessage = useCallback((params) => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((resolve, reject) => {
            try {
                if (!isConnected) {
                    reject(new Error('WebSocket not connected'));
                    return;
                }
                // Create a unique message key to prevent duplicates
                const messageKey = `${params.conversationId}_${params.text}_${Date.now()}`;
                // Check if this message was already sent recently
                if (sentMessagesRef.current.has(messageKey)) {
                    reject(new Error('Message already sent'));
                    return;
                }
                sentMessagesRef.current.add(messageKey);
                // Clean up old message keys
                if (sentMessagesRef.current.size > 10) {
                    const keys = Array.from(sentMessagesRef.current);
                    sentMessagesRef.current.clear();
                    keys.slice(-5).forEach((key) => sentMessagesRef.current.add(key));
                }
                const messageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
                // Store the promise resolvers
                pendingMessages.current.set(messageId, { resolve, reject });
                // Send the message
                const message = {
                    type: 'send_message',
                    id: messageId,
                    payload: {
                        conversationId: params.conversationId,
                        text: params.text,
                        image: params.image || null,
                        mentions: params.mentions || null,
                    },
                };
                sendWebSocketMessage(message);
                // Set a timeout for the request
                setTimeout(() => {
                    const pending = pendingMessages.current.get(messageId);
                    if (pending) {
                        pending.reject(new Error('Request timeout'));
                        pendingMessages.current.delete(messageId);
                    }
                }, 10000); // 10 second timeout
            }
            catch (error) {
                reject(error);
            }
        });
    }), [isConnected, sendWebSocketMessage]);
    return {
        sendMessage,
        isConnected,
    };
};
export default useWebSocketMessage;
